'use client';
import {cn} from '@/lib/utils';
import {StrapiMenu, StrapiMenuItem} from '@/types/strapi';
import Link from 'next/link';
import React, {useEffect, useState} from 'react';
import {target} from './helpers/utils';

const NavigationSubMenu: React.FC<StrapiMenu> = ({
  lang,
  menu = [],
  title = ''
}) => {
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [openSubMenu, setOpenSubMenu] = useState<string | null>(null);
  const [openMobileMenu, setOpenMobileMenu] = useState(false);
  const [currentPath, setCurrentPath] = useState('');
  const [isShowIcon, setIsShowIcon] = useState(true);
  let lastY = 0;

  const toggleMenu = (name: string) => {
    setOpenMenu(openMenu === name ? null : name);
    setOpenSubMenu(null); // Reset submenu when main menu toggles
  };

  const toggleSubMenu = (name: string) => {
    setOpenSubMenu(openSubMenu === name ? null : name);
  };

  useEffect(() => {
    setCurrentPath(window.location.pathname.replace(lang + '/', ''));
    window.addEventListener('scroll', () => {
      if (window.scrollY < lastY) {
        setIsShowIcon(true);
      } else {
        setIsShowIcon(false);
      }
      lastY = window.scrollY;
    });
  }, []);

  return (
    <div className={cn('relative', lang == 'en' ? 'questrial' : 'jhenghei')}>
      {isShowIcon && (
        <div className="md:hidden flex justify-end items-center ">
          <button
            onClick={() => {
              setOpenMobileMenu(!openMobileMenu);
              // check sub-menu-title height
              setTimeout(() => {
                const subMenuTitle = document.getElementById('sub-menu-title');
                if (subMenuTitle) {
                  const top =
                    subMenuTitle.getBoundingClientRect().top + window.scrollY;
                  window.scrollTo({top: top, behavior: 'smooth'});
                } else {
                  window.scrollTo({top: 0, behavior: 'smooth'});
                }
              }, 100);
            }}
            className={cn(
              'py-2 flex items-center justify-center cursor-pointer fixed z-10 rounded-full ',
              'z-50 bottom-40 right-10 h-10 w-10 fade-in-up text-white bg-[#e22319]',
              'hover:bg-white hover:text-[#e22319] shadow-lg transition-all duration-300 ease-in-out'
            )}
          >
            {openMobileMenu ? (
              <div className="material-symbols outlined">close</div>
            ) : (
              <div className="material-symbols outlined">menu</div>
            )}
          </button>
        </div>
      )}

      <nav
        className={cn(
          'container mx-auto flex-col md:flex justify-between bg-white ml-4 md:ml-0 md:px-4',
          !openMobileMenu && 'hidden'
        )}
        aria-label="Navigation Sub Menu"
      >
        {title && (
          <div
            className="text-[#e22319] text-[24px] font-bold pb-4 pt-4 md:pt-0  "
            id="sub-menu-title"
          >
            {title}
          </div>
        )}
        {menu.map((item: StrapiMenuItem) => (
          <div
            key={item.name}
            className={cn('relative group')}
            onMouseEnter={() => {
              if (window.innerWidth > 768) {
                setOpenMenu(item.name);
              }
            }}
            onMouseLeave={() => {
              if (window.innerWidth > 768) {
                setOpenMenu(null);
              }
            }}
          >
            <Link
              prefetch={false}
              href={item.url}
              target={target(item.url)}
              locale={lang}
            >
              <div
                className={cn(
                  'py-1 px-4 flex items-center justify-between w-full text-left border-b border-[#efefff] cursor-pointer hover:bg-[#eeeeee]',
                  item.url === currentPath
                    ? 'text-[#e22319]'
                    : 'hover:text-[#e22319]'
                )}
                onClick={() => toggleMenu(item.name)}
              >
                {item.name}
              </div>
            </Link>
            {item.items && (
              <div
                className={`md:group-hover:block left-0 bg-white w-full z-10 md:rounded-b-lg`}
              >
                {item.items.map((subItem) => (
                  <div key={subItem.name} className={cn('relative group')}>
                    <div className="flex items-center">
                      <Link
                        prefetch={false}
                        href={subItem.url}
                        target={target(subItem.url)}
                        className={cn(
                          'block py-1 px-4 pl-8 w-full text-left border-b border-[#efefff] hover:bg-[#eeeeee]',
                          subItem.url === currentPath
                            ? 'text-[#e22319]'
                            : 'hover:text-[#e22319]'
                        )}
                        locale={lang}
                      >
                        {subItem.name}
                      </Link>
                      {subItem.items && (
                        <button
                          className="md:hidden p-1"
                          onClick={() => toggleSubMenu(subItem.name)}
                        ></button>
                      )}
                    </div>
                    {subItem.items && (
                      <div className={` `}>
                        {subItem.items.map((nestedItem) => (
                          <Link
                            prefetch={false}
                            key={nestedItem.name}
                            href={nestedItem.url}
                            target={target(nestedItem.url)}
                            className={cn(
                              'block py-1 px-4 pl-8 hover:bg-[#eeeeee]',
                              nestedItem.url === currentPath
                                ? 'text-[#e22319]'
                                : 'hover:text-[#e22319]'
                            )}
                            locale={lang}
                          >
                            {nestedItem.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
        <hr className="hidden border-gray-200 my-4 md:hidden w-[90%] m-auto" />
      </nav>
    </div>
  );
};

export default NavigationSubMenu;
